import { Injectable } from '@angular/core';
import { SwPush } from '@angular/service-worker';
import { PushService } from 'src/app/proxy/push-notifications';
import { User } from '../entities/user';
import { ToasterService } from '@abp/ng.theme.shared';
import { UserRepository } from '../local-db/repositries/user-repository';
import { firstValueFrom } from 'rxjs';
import { OrderType } from 'src/app/proxy/orders/enums';

@Injectable({ providedIn: 'root' })
export class PushNotificationService {
  private listenersRegistered = false;
  private initializing = false;

  constructor(
    private readonly swPush: SwPush,
    private readonly pushService: PushService, 
    private readonly toasterService: ToasterService,
    private readonly userRepository: UserRepository,
  ) {}

    async init(user: User): Promise<void> {
        if (!this.swPush.isEnabled) {
            return;
        }
        if (!user?.id) {
            return;
        }
        if (this.initializing) {
            return;
        }
        this.initializing = true;

        try {
            const existingSub = await firstValueFrom(this.swPush.subscription);
            if (existingSub) {
                const json = existingSub.toJSON();

                if (user.pushEndpoint !== json.endpoint) {
                    await this.pushService.subscribe({
                        userId   : user.id,
                        endpoint : json.endpoint,
                        p256dh   : json.keys!['p256dh'],
                        auth     : json.keys!['auth'],
                    }).toPromise();

                    await this.userRepository.setEndpoint(user.email, json.endpoint);
                }

            } else {
                const vapidKey = await firstValueFrom(this.pushService.getVapidPublicKey());
                const sub = await this.swPush.requestSubscription({ serverPublicKey: vapidKey });
                const json = sub.toJSON();

                await firstValueFrom(this.pushService.subscribe({
                    userId   : user.id,
                    endpoint : json.endpoint,
                    p256dh   : json.keys!['p256dh'],
                    auth     : json.keys!['auth'],
                }));

                await this.userRepository.setEndpoint(user.email, json.endpoint);
            }
        }
        catch{
            return;
        }
        finally {
            this.initializing = false;
        }
    }

    public registerListeners() {
        if (!this.listenersRegistered) {
            this.listenersRegistered = true;
            this.swPush.notificationClicks.subscribe(({ notification }) => {
                const { orderId, orderType } = notification.data;
                if (orderId) {
                    const path = orderType === OrderType.PurchaseOrder
                        ? `/purchase-order/${orderId}`
                        : `/order/${orderId}`;
                    window.open(path, '_self');
                }
            });

            this.swPush.messages.subscribe((msg: any) => {
                const { title, message } = msg;
                this.toasterService.info(message, title);
            });
        }
    }

    async logout(user: User): Promise<void> {
        try {
            const endpoint = user.pushEndpoint;
            if (endpoint) {
                await this.pushService.unsubscribe({ endpoint }).toPromise();
                await this.swPush.unsubscribe();
                await this.userRepository.setEndpoint(user.email, null);
            }
        } catch (err) {
        }
    }
}